import React from 'react';

import imgControl from '../../assets/manual/web-control.png';
import imgMenu from '../../assets/manual/web-menu.png';
import imgSettings from '../../assets/manual/web-settings.png';
import imgSponsors from '../../assets/manual/web-settings-sponsors.png';
import imgPenalty from '../../assets/manual/web-penalty.png';
import imgLiveview from '../../assets/manual/web-liveview.png';
import imgLicense from '../../assets/manual/web-settings-license.png';
import imgEntSettings from '../../assets/manual/web-entertainment-settings.png';
import imgMediaMenu from '../../assets/manual/web-media-menu.png';
import imgMultimedia from '../../assets/manual/web-multimedia.png';

// Two beginner's guides for the browser edition, in the same shape as the desktop one: one action
// per step, and a picture of anything the reader has to find on screen.
//
//   variant="free"          the scoreboard as everybody gets it, no licence
//   variant="entertainment" what a media licence adds — music and video, all of it hand-driven
//
// The second guide is deliberately blunt about the manual part. An operator who expects the music to
// take care of itself discovers otherwise in front of a full hall, and that is a bad place for it.

const content = {
  free: {
    de: {
      intro: {
        title: 'Für wen ist diese Anleitung?',
        text: 'Für alle, die zum ersten Mal am Scoreboard sitzen. Du brauchst nur einen Computer mit Chrome und die Adresse der Seite — nichts zu installieren, kein Konto, keine Kosten.',
        hint: 'Diese Anleitung beschreibt die kostenlose Web-Version. Musik und Videos sind hier nicht dabei — dafür gibt es den zweiten Reiter.',
      },
      sections: [
        {
          id: 'w-start',
          title: '1. Scoreboard öffnen',
          lead: 'Du arbeitest mit zwei Fenstern: einem zum Bedienen, einem für die Zuschauer.',
          steps: [
            'Öffne die Seite und klicke auf „Scoreboard starten". Das ist dein Bedienfenster.',
            'Unten links öffnest du das Menü und klickst auf „Live View öffnen". Es geht ein zweites Fenster auf.',
            'Ziehe dieses zweite Fenster auf den Beamer oder den Fernseher und schalte es auf Vollbild (Taste F11).',
          ],
          image: imgControl,
          caption: 'Das Bedienfenster: links das Heimteam, rechts das Gastteam, in der Mitte Uhr und Periode.',
          tip: 'Beide Fenster zeigen immer denselben Stand. Was du links änderst, erscheint sofort auf dem grossen Bildschirm.',
        },
        {
          id: 'w-teams',
          title: '2. Teamnamen eintragen',
          lead: 'In der kostenlosen Version tippst du die Namen direkt ein.',
          steps: [
            'Klicke auf „HEIMTEAM" und schreibe den richtigen Namen.',
            'Dasselbe rechts bei „GASTTEAM".',
            'Klicke daneben, damit der Name übernommen wird.',
          ],
          tip: 'Wenn du regelmässig gegen dieselben Vereine spielst und die Wappen dazu möchtest, ist das der Punkt, an dem sich die Desktop-Edition lohnt: dort wählst du gespeicherte Teams aus, statt zu tippen.',
        },
        {
          id: 'w-spiel',
          title: '3. Das Spiel führen',
          lead: 'Alles, was du während des Spiels brauchst, liegt auf der Hauptseite.',
          steps: [
            'START drückt die Uhr los, STOP hält sie an.',
            'Tore zählst du mit den Knöpfen + und − über der Zahl.',
            'Teamfouls zählst du mit den Knöpfen unter dem Team. Ab dem 10. Foul färbt sich die Anzeige.',
            'Die Periode wechselst du in der Mitte mit + und −. Meist macht das die Uhr von selbst.',
          ],
          tip: 'Vertippt? Alle Knöpfe funktionieren in beide Richtungen. Du kannst jede Zahl auch wieder heruntersetzen.',
        },
        {
          id: 'w-strafen',
          title: '4. Strafen erfassen',
          lead: 'Eine Strafe läuft rückwärts und verschwindet von selbst, wenn sie abgelaufen ist.',
          steps: [
            'Klicke beim betroffenen Team auf „STRAFE HINZUFÜGEN".',
            'Gib die Nummer der Spielerin und die Dauer ein.',
            'Die Strafe erscheint in der Mitte und läuft, sobald die Spieluhr läuft.',
          ],
          image: imgPenalty,
          caption: 'Das Fenster zum Erfassen einer Strafe.',
          tip: 'Hat ein Team mehrere Strafen gleichzeitig, rechnet die Anzeige die Unterzahlzeit selbst zusammen — du musst nichts addieren.',
        },
        {
          id: 'w-menu',
          title: '5. Das Menü',
          lead: 'Unten links versteckt sich alles, was du nur ab und zu brauchst.',
          image: imgMenu,
          caption: 'Live View öffnen, Buzzer, Pause, Zurücksetzen und die Einstellungen.',
          steps: [
            '„Pause" startet den Pausen-Timer zwischen zwei Perioden.',
            '„Buzzer" löst das Horn von Hand aus.',
            '„Zurücksetzen" stellt das Spiel auf null — frag lieber zweimal, bevor du das im laufenden Spiel drückst.',
          ],
        },
        {
          id: 'w-einstellungen',
          title: '6. Einstellungen',
          lead: 'Einmal einrichten, dann stimmt es für die Saison.',
          steps: [
            'Menü → „Einstellungen".',
            'Stelle unter „Allgemeine Einstellungen" die Spielzeit und die Pausendauer ein.',
            'Wähle die Markenfarbe — das ist die Farbe der Anzeigetafel.',
            'Stelle die Sprache getrennt für Bedienung und Anzeige ein.',
          ],
          image: imgSettings,
          caption: 'Die Einstellungen. Alles wird sofort gespeichert.',
        },
        {
          id: 'w-sponsoren',
          title: '7. Sponsoren zeigen',
          lead: 'Bis zu sechs Logos wechseln sich auf dem Zuschauerbildschirm ab.',
          steps: [
            'Einstellungen → Reiter „Sponsoren".',
            'Lade pro Feld ein Bild hoch.',
            'Fertig — die Logos laufen automatisch durch, wenn gerade keine Strafe angezeigt wird.',
          ],
          image: imgSponsors,
          caption: 'Die Sponsorenfelder.',
        },
        {
          id: 'w-liveview',
          title: '8. Das sehen die Zuschauer',
          lead: 'So sieht der grosse Bildschirm aus.',
          image: imgLiveview,
          caption: 'Die Zuschaueransicht mit Vereinsfarbe und Sponsorenlogo.',
          tip: 'Prüfe vor dem Spiel einmal aus der letzten Zuschauerreihe, ob alles gut lesbar ist.',
        },
      ],
    },
    en: {
      intro: {
        title: 'Who is this guide for?',
        text: 'For anyone sitting at the scoreboard for the first time. All you need is a computer with Chrome and the address of the page — nothing to install, no account, no cost.',
        hint: 'This guide covers the free web edition. Music and video are not part of it — that is the second tab.',
      },
      sections: [
        {
          id: 'w-start',
          title: '1. Opening the scoreboard',
          lead: 'You work with two windows: one to operate, one for the audience.',
          steps: [
            'Open the page and click “Open scoreboard”. That is your control window.',
            'Open the menu at the bottom left and click “Open Live View”. A second window opens.',
            'Drag that second window onto the projector or TV and put it full screen (F11).',
          ],
          image: imgControl,
          caption: 'The control window: home team left, away team right, clock and period in the middle.',
          tip: 'Both windows always show the same thing. What you change on the left appears on the big screen at once.',
        },
        {
          id: 'w-teams',
          title: '2. Entering team names',
          lead: 'In the free edition you type the names directly.',
          steps: ['Click “HOME TEAM” and write the real name.', 'Do the same on the right for “AWAY TEAM”.', 'Click next to it so the name is taken over.'],
          tip: 'If you play the same clubs regularly and want their crests, this is where the desktop edition pays off: there you pick stored teams instead of typing.',
        },
        {
          id: 'w-spiel',
          title: '3. Running the match',
          lead: 'Everything you need during the match is on the main page.',
          steps: [
            'START runs the clock, STOP holds it.',
            'Count goals with the + and − buttons above the number.',
            'Count team fouls with the buttons under the team. From the 10th foul the display changes colour.',
            'Change the period in the middle with + and −. Usually the clock does that by itself.',
          ],
          tip: 'Mistyped? Every button works both ways. You can always count back down.',
        },
        {
          id: 'w-strafen',
          title: '4. Entering penalties',
          lead: 'A penalty counts down and disappears by itself when it has run out.',
          steps: ['Click “ADD PENALTY” on the team concerned.', 'Enter the player number and the duration.', 'The penalty appears in the middle and runs as soon as the game clock runs.'],
          image: imgPenalty,
          caption: 'The dialog for entering a penalty.',
          tip: 'If a team has several penalties at once, the board works out the short-handed time for you — no adding up.',
        },
        {
          id: 'w-menu',
          title: '5. The menu',
          lead: 'The bottom left hides everything you only need now and then.',
          image: imgMenu,
          caption: 'Open Live View, buzzer, break, reset and the settings.',
          steps: ['“Break” starts the break timer between periods.', '“Buzzer” sounds the horn by hand.', '“Reset” puts the match back to zero — think twice before pressing that during a match.'],
        },
        {
          id: 'w-einstellungen',
          title: '6. Settings',
          lead: 'Set this up once and it is right for the season.',
          steps: [
            'Menu → Settings.',
            'Set the period length and the break length under General.',
            'Pick the brand colour — that is the colour of the board.',
            'Set the language separately for the controls and for the display.',
          ],
          image: imgSettings,
          caption: 'The settings. Everything saves immediately.',
        },
        {
          id: 'w-sponsoren',
          title: '7. Showing sponsors',
          lead: 'Up to six logos take turns on the audience screen.',
          steps: ['Settings → Sponsors tab.', 'Upload one image per slot.', 'Done — the logos rotate by themselves whenever no penalty is on screen.'],
          image: imgSponsors,
          caption: 'The sponsor slots.',
        },
        {
          id: 'w-liveview',
          title: '8. What the audience sees',
          lead: 'This is the big screen.',
          image: imgLiveview,
          caption: 'The audience view with club colour and sponsor logo.',
          tip: 'Before the match, check once from the back row that everything is readable.',
        },
      ],
    },
  },

  entertainment: {
    de: {
      intro: {
        title: 'Was die Medien-Lizenz dazugibt',
        text: 'Mit einer Medien-Lizenz kannst du im Browser Musik und Videos abspielen: ein Loop-Video vor dem Spiel, ein Intro zum Einlaufen, und bis zu zehn Songs während des Spiels.',
        hint: 'Wichtig vorweg: Hier läuft nichts von selbst. Jeden Song und jedes Video startest und stoppst du selbst — auch mitten im Spiel. Wer das automatisch möchte, ist bei der Desktop-Edition richtig.',
      },
      sections: [
        {
          id: 'e-lizenz',
          title: '1. Lizenz aktivieren',
          steps: [
            'Menü unten links → „Einstellungen".',
            'Reiter „Medien Lizenz" öffnen.',
            'Vereinsnamen und Lizenzschlüssel eintragen, den du von uns bekommen hast.',
            'Auf „Aktivieren" klicken. Der Status wechselt auf grün.',
          ],
          image: imgLicense,
          caption: 'Der Reiter „Medien Lizenz". Ohne gültigen Schlüssel bleiben die Medienfunktionen ausgeblendet.',
        },
        {
          id: 'e-medien',
          title: '2. Das neue Medien-Menü',
          lead: 'Mit aktiver Lizenz erscheint unten rechts ein zweiter Knopf: „Medien".',
          image: imgMediaMenu,
          caption: 'Links das gewohnte Menü fürs Spiel, rechts das neue für Musik und Video.',
          tip: 'Merk dir die Aufteilung: links das Spiel, rechts die Unterhaltung. Während des Spiels brauchst du beide.',
        },
        {
          id: 'e-hochladen',
          title: '3. Musik und Videos hinterlegen',
          lead: 'Das machst du in Ruhe vor dem Spieltag — nicht zwischen Tür und Angel.',
          steps: [
            'Medien → „Einstellungen".',
            'Oben bei „Multimedia Videos": bis zu vier Videos hochladen. Setze bei einem Video den Haken „Video wiederholen", wenn es als Endlosschleife laufen soll.',
            'Optional die YouTube-Playlist-Adresse eintragen — die läuft vor dem Spiel.',
            'Unten bei „In-Game": bis zu zehn MP3-Dateien in die Slots laden.',
          ],
          image: imgEntSettings,
          caption: 'Die Entertainment-Einstellungen: oben Videos, unten die Song-Slots.',
          tip: 'Gib den Dateien vorher sprechende Namen. Im Eifer des Gefechts willst du „Torhymne" lesen und nicht „track_03_final_v2".',
        },
        {
          id: 'e-pregame',
          title: '4. Vor dem Spiel: Pre-Game',
          lead: 'Der Multimedia-Dialog führt dich durch den Ablauf.',
          steps: [
            'Medien → „Multimedia".',
            'Etwa 30 Minuten vor dem Spiel: Loop-Video und Musik starten.',
            'Etwa 5 Minuten vor dem Spiel: Loop-Video stoppen, Intro-Video starten.',
            'Das Video öffnet sich in einem eigenen Fenster — schiebe es auf den Beamer und schalte auf Vollbild.',
          ],
          image: imgMultimedia,
          caption: 'Der Multimedia-Dialog mit dem vorgeschlagenen Ablauf.',
        },
        {
          id: 'e-ingame',
          title: '5. Während des Spiels: Musik',
          lead: 'Über „Medien → In-Game" öffnest du den Player mit deinen zehn Songs.',
          steps: [
            'Bei einer Unterbrechung den gewünschten Song anklicken.',
            'Wenn das Spiel weitergeht: Song von Hand stoppen.',
            'Nach einem Tor: die Torhymne anklicken und wieder stoppen.',
          ],
          tip: 'Das ist der Punkt, an dem Übung zählt. Plane jemanden ein, der sich nur darum kümmert — oder nimm die Desktop-Edition, die genau das automatisch macht.',
        },
        {
          id: 'e-grenzen',
          title: '6. Was diese Version nicht kann',
          lead: 'Damit du nicht am Spieltag danach suchst:',
          steps: [
            'Nichts startet oder stoppt von selbst — auch nicht bei Spielunterbruch oder Tor.',
            'Keine Teamlogos und keine hinterlegten Kader.',
            'Keine Tor-Animation und keine Torjubel-Videos pro Spielerin.',
            'Videos laufen in einem separaten Fenster, nicht in die Anzeigetafel eingebettet.',
          ],
        },
      ],
    },
    en: {
      intro: {
        title: 'What the media licence adds',
        text: 'With a media licence you can play music and video in the browser: a loop video before the match, an intro for the entrance, and up to ten songs during the match.',
        hint: 'One thing up front: nothing here runs by itself. You start and stop every song and every video yourself, including during the match. If you want that automatic, the desktop edition is the one.',
      },
      sections: [
        {
          id: 'e-lizenz',
          title: '1. Activating the licence',
          steps: [
            'Menu at the bottom left → Settings.',
            'Open the “Media licence” tab.',
            'Enter the club name and the licence key you received from us.',
            'Click Activate. The status turns green.',
          ],
          image: imgLicense,
          caption: 'The media licence tab. Without a valid key the media features stay hidden.',
        },
        {
          id: 'e-medien',
          title: '2. The new media menu',
          lead: 'With an active licence a second button appears at the bottom right: Media.',
          image: imgMediaMenu,
          caption: 'The familiar match menu on the left, the new one for music and video on the right.',
          tip: 'Remember the split: left is the match, right is the entertainment. During a match you need both.',
        },
        {
          id: 'e-hochladen',
          title: '3. Loading music and video',
          lead: 'Do this calmly before match day, not in passing.',
          steps: [
            'Media → Settings.',
            'Under Multimedia Videos: upload up to four videos. Tick “repeat video” on the one that should loop.',
            'Optionally enter the YouTube playlist address — that plays before the match.',
            'Under In-Game: load up to ten MP3 files into the slots.',
          ],
          image: imgEntSettings,
          caption: 'The entertainment settings: videos at the top, song slots below.',
          tip: 'Give the files meaningful names first. In the heat of the moment you want to read “goal anthem”, not “track_03_final_v2”.',
        },
        {
          id: 'e-pregame',
          title: '4. Before the match: pre-game',
          lead: 'The multimedia dialog walks you through it.',
          steps: [
            'Media → Multimedia.',
            'About 30 minutes before: start the loop video and the music.',
            'About 5 minutes before: stop the loop, start the intro video.',
            'The video opens in a window of its own — move it to the projector and go full screen.',
          ],
          image: imgMultimedia,
          caption: 'The multimedia dialog with the suggested running order.',
        },
        {
          id: 'e-ingame',
          title: '5. During the match: music',
          lead: 'Media → In-Game opens the player with your ten songs.',
          steps: ['On a stoppage, click the song you want.', 'When play resumes, stop the song by hand.', 'After a goal, click the anthem and stop it again.'],
          tip: 'This is where practice counts. Plan for somebody who does only this — or take the desktop edition, which does exactly this automatically.',
        },
        {
          id: 'e-grenzen',
          title: '6. What this edition cannot do',
          lead: 'So you do not go looking for it on match day:',
          steps: [
            'Nothing starts or stops by itself — not on a stoppage and not on a goal.',
            'No team logos and no stored rosters.',
            'No goal animation and no per-player celebration videos.',
            'Videos play in a separate window, not woven into the board.',
          ],
        },
      ],
    },
  },
};

function Figure({ src, caption }) {
  return (
    <figure className="mt-5">
      <img src={src} alt={caption} loading="lazy" className="w-full rounded-lg border border-gray-200 shadow-sm" />
      <figcaption className="mt-2 text-sm text-gray-500">{caption}</figcaption>
    </figure>
  );
}

export default function WebManual({ variant = 'free', lang = 'de' }) {
  const c = (content[variant] || content.free)[lang] || (content[variant] || content.free).de;

  return (
    <>
      <section className="mb-10 rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h2 className="mb-2 text-2xl font-semibold text-blue-900">{c.intro.title}</h2>
        <p className="text-blue-900">{c.intro.text}</p>
        <p className="mt-3 text-sm font-medium text-blue-800">{c.intro.hint}</p>
      </section>

      <nav className="mb-12 rounded-lg border border-gray-200 p-5">
        <ol className="grid gap-x-6 gap-y-2 text-sm md:grid-cols-2">
          {c.sections.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-blue-600 hover:text-blue-800">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {c.sections.map((s) => (
        <section key={s.id} id={s.id} className="mb-12 scroll-mt-8">
          <h2 className="mb-3 text-2xl font-semibold">{s.title}</h2>
          {s.lead && <p className="mb-4 text-lg text-gray-700">{s.lead}</p>}
          {s.steps && (
            <ol className="list-decimal space-y-2 pl-6">
              {s.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}
          {s.image && <Figure src={s.image} caption={s.caption} />}
          {s.tip && (
            <div className="mt-5 border-l-4 border-amber-400 bg-amber-50 p-4 text-amber-900">
              <span className="font-semibold">{lang === 'de' ? 'Tipp: ' : 'Tip: '}</span>
              {s.tip}
            </div>
          )}
        </section>
      ))}
    </>
  );
}
